import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  ConnectionState,
  Room,
  RoomEvent,
  Track,
  type RemoteParticipant,
  type TranscriptionSegment,
} from 'livekit-client';

export interface LiveKitTranscriptEvent {
  role: 'ai' | 'customer';
  text: string;
  time: string;
  final: boolean;
  participantIdentity?: string;
}

@Component({
  selector: 'app-livekit-room',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './livekit-room.component.html',
  styleUrl: './livekit-room.component.css',
})
export class LiveKitRoomComponent implements OnChanges, OnDestroy {
  /** LiveKit WebSocket URL (wss://….livekit.cloud) */
  @Input() serverUrl = '';
  @Input() token = '';
  @Input() active = false;
  /** Increment to force a reconnect (e.g. user clicked Enable Camera). */
  @Input() connectTrigger = 0;
  /** Identity prefixes treated as AI/agent for transcript role mapping */
  @Input() aiIdentityPrefixes: string[] = ['host', 'agent', 'ai'];

  @Output() transcript = new EventEmitter<LiveKitTranscriptEvent>();
  @Output() connectedChange = new EventEmitter<boolean>();
  @Output() connectionError = new EventEmitter<string>();
  /** Fired when the call ends (disconnect, room closed, or last guest left). */
  @Output() sessionEnded = new EventEmitter<void>();

  @ViewChild('localVideo', { static: true }) localVideoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('remoteVideo', { static: true }) remoteVideoRef!: ElementRef<HTMLVideoElement>;

  connecting = false;
  connected = false;
  private room: Room | null = null;
  private connectGen = 0;
  private readonly segmentBuffer = new Map<string, string>();
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private recordingCanvas: HTMLCanvasElement | null = null;
  private recordingRaf = 0;
  private recordingStream: MediaStream | null = null;
  private sessionEndedEmitted = false;
  private hadActiveCall = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['active'] || changes['token'] || changes['serverUrl'] || changes['connectTrigger']) {
      if (this.active && this.serverUrl && this.token) {
        void this.connect();
      } else if (!this.active) {
        void this.disconnect();
      }
    }
  }

  ngOnDestroy(): void {
    void this.disconnect();
  }

  /** Local camera preview element (for KYC frame capture, etc.). */
  getLocalVideoElement(): HTMLVideoElement | null {
    return this.localVideoRef?.nativeElement ?? null;
  }

  async connect(): Promise<void> {
    if (!this.serverUrl || !this.token) return;

    const gen = ++this.connectGen;
    await this.disconnect(false);
    if (gen !== this.connectGen) return;

    this.connecting = true;
    this.sessionEndedEmitted = false;
    this.hadActiveCall = false;
    const room = new Room({ adaptiveStream: true, dynacast: true });
    this.room = room;

    room.on(RoomEvent.LocalTrackPublished, (pub) => {
      if (pub.source === Track.Source.Camera && pub.track) {
        pub.track.attach(this.localVideoRef.nativeElement);
        this.connectedChange.emit(true);
      }
    });

    room.on(RoomEvent.TrackSubscribed, (track, _pub, participant) => {
      if (track.kind === Track.Kind.Video) {
        const el = this.pickRemoteVideoElement(participant);
        track.attach(el);
      }
      if (track.kind === Track.Kind.Audio) {
        track.attach();
      }
    });

    room.on(RoomEvent.TrackUnsubscribed, (track) => {
      track.detach();
    });

    room.on(RoomEvent.TranscriptionReceived, (segments: TranscriptionSegment[], participant) => {
      this.emitSegments(segments, participant?.identity);
    });

    try {
      room.registerTextStreamHandler('lk.transcription', async (reader, info) => {
        const identity = info?.identity ?? '';
        let text = '';
        for await (const chunk of reader) {
          text += typeof chunk === 'string' ? chunk : new TextDecoder().decode(chunk);
        }
        const trimmed = text.trim();
        if (trimmed) {
          this.transcript.emit({
            role: this.roleFromIdentity(identity),
            text: trimmed,
            time: this.nowTime(),
            final: true,
            participantIdentity: identity,
          });
        }
      });
    } catch {
      /* handler may already exist on reconnect */
    }

    room.on(RoomEvent.ConnectionStateChanged, (state: ConnectionState) => {
      const isConnected = state === ConnectionState.Connected;
      this.connected = isConnected;
      this.connectedChange.emit(isConnected);
      if (state === ConnectionState.Disconnected) {
        this.emitSessionEnded();
      }
    });

    room.on(RoomEvent.Disconnected, () => {
      this.emitSessionEnded();
    });

    room.on(RoomEvent.ParticipantDisconnected, () => {
      if (room.remoteParticipants.size === 0) {
        this.emitSessionEnded();
      }
    });

    try {
      await room.connect(this.serverUrl, this.token);
      if (gen !== this.connectGen) {
        room.disconnect();
        return;
      }
      await room.localParticipant.setCameraEnabled(true);
      await room.localParticipant.setMicrophoneEnabled(true);

      const camPub = room.localParticipant.getTrackPublication(Track.Source.Camera);
      const localTrack = camPub?.track;
      if (localTrack) {
        localTrack.attach(this.localVideoRef.nativeElement);
      }

      this.connected = true;
      this.hadActiveCall = true;
      this.connectedChange.emit(true);
      this.startSessionRecording(room);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to connect to LiveKit';
      this.connectionError.emit(msg);
      this.connected = false;
      this.connectedChange.emit(false);
    } finally {
      this.connecting = false;
    }
  }

  /** Stop canvas capture and return the recorded WebM blob (if any). */
  async stopSessionRecording(): Promise<Blob | null> {
    if (this.recordingRaf) {
      cancelAnimationFrame(this.recordingRaf);
      this.recordingRaf = 0;
    }

    const recorder = this.mediaRecorder;
    if (!recorder || recorder.state === 'inactive') {
      this.cleanupRecordingResources();
      const chunks = this.recordedChunks;
      this.recordedChunks = [];
      return chunks.length ? new Blob(chunks, { type: 'video/webm' }) : null;
    }

    return new Promise<Blob | null>((resolve) => {
      recorder.onstop = () => {
        const blob = this.recordedChunks.length
          ? new Blob(this.recordedChunks, { type: recorder.mimeType || 'video/webm' })
          : null;
        this.recordedChunks = [];
        this.mediaRecorder = null;
        this.cleanupRecordingResources();
        resolve(blob);
      };
      try {
        recorder.stop();
      } catch {
        this.cleanupRecordingResources();
        resolve(null);
      }
    });
  }

  async disconnect(emitDisconnected = true): Promise<void> {
    this.connectGen++;
    this.connecting = false;
    if (this.mediaRecorder?.state === 'recording') {
      try {
        this.mediaRecorder.stop();
      } catch {
        /* ignore */
      }
    }
    this.cleanupRecordingResources();
    const room = this.room;
    this.room = null;
    this.connected = false;
    if (emitDisconnected) {
      this.connectedChange.emit(false);
    }
    this.segmentBuffer.clear();
    if (room) {
      room.removeAllListeners();
      room.disconnect();
    }
    const localEl = this.localVideoRef?.nativeElement;
    if (localEl) {
      localEl.srcObject = null;
    }
    const remoteEl = this.remoteVideoRef?.nativeElement;
    if (remoteEl) {
      remoteEl.srcObject = null;
    }
  }

  private pickRemoteVideoElement(_participant: RemoteParticipant): HTMLVideoElement {
    return this.remoteVideoRef.nativeElement;
  }

  private emitSegments(segments: TranscriptionSegment[], identity?: string): void {
    const role = this.roleFromIdentity(identity ?? '');
    for (const seg of segments) {
      const text = (seg.text ?? '').trim();
      if (!text) continue;
      const key = `${identity ?? 'local'}:${seg.id ?? text}`;
      if (seg.final) {
        this.segmentBuffer.delete(key);
        this.transcript.emit({
          role,
          text,
          time: this.nowTime(),
          final: true,
          participantIdentity: identity,
        });
      } else {
        this.segmentBuffer.set(key, text);
        this.transcript.emit({
          role,
          text,
          time: this.nowTime(),
          final: false,
          participantIdentity: identity,
        });
      }
    }
  }

  private roleFromIdentity(identity: string): 'ai' | 'customer' {
    const id = identity.toLowerCase();
    if (this.aiIdentityPrefixes.some((p) => id.startsWith(p.toLowerCase()))) {
      return 'ai';
    }
    return 'customer';
  }

  private nowTime(): string {
    return new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  }

  private emitSessionEnded(): void {
    if (this.sessionEndedEmitted || !this.hadActiveCall) return;
    this.sessionEndedEmitted = true;
    this.sessionEnded.emit();
  }

  private resetActiveRecording(): void {
    if (this.recordingRaf) {
      cancelAnimationFrame(this.recordingRaf);
      this.recordingRaf = 0;
    }
    if (this.mediaRecorder?.state === 'recording') {
      try {
        this.mediaRecorder.stop();
      } catch {
        /* ignore */
      }
    }
    this.mediaRecorder = null;
    this.recordedChunks = [];
    this.cleanupRecordingResources();
  }

  private startSessionRecording(room: Room): void {
    this.resetActiveRecording();

    const canvas = document.createElement('canvas');
    canvas.width = 1280;
    canvas.height = 720;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const draw = () => {
      ctx.fillStyle = '#0f172a';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const local = this.localVideoRef?.nativeElement;
      const remote = this.remoteVideoRef?.nativeElement;
      if (local?.videoWidth) {
        ctx.drawImage(local, 0, 0, canvas.width / 2, canvas.height);
      }
      if (remote?.videoWidth) {
        ctx.drawImage(remote, canvas.width / 2, 0, canvas.width / 2, canvas.height);
      }
      this.recordingRaf = requestAnimationFrame(draw);
    };
    draw();

    const stream = canvas.captureStream(12);
    const micTrack = room.localParticipant
      .getTrackPublication(Track.Source.Microphone)
      ?.track?.mediaStreamTrack;
    if (micTrack) {
      stream.addTrack(micTrack);
    }
    this.recordingCanvas = canvas;
    this.recordingStream = stream;

    const mime = this.resolveRecordingMimeType();
    try {
      const recorder = mime
        ? new MediaRecorder(stream, { mimeType: mime })
        : new MediaRecorder(stream);
      recorder.ondataavailable = (ev) => {
        if (ev.data?.size) this.recordedChunks.push(ev.data);
      };
      recorder.start(1000);
      this.mediaRecorder = recorder;
    } catch {
      this.cleanupRecordingResources();
    }
  }

  private cleanupRecordingResources(): void {
    this.recordingStream?.getTracks().forEach((t) => t.stop());
    this.recordingStream = null;
    this.recordingCanvas = null;
  }

  private resolveRecordingMimeType(): string {
    if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
      return 'video/webm;codecs=vp9';
    }
    if (MediaRecorder.isTypeSupported('video/webm')) {
      return 'video/webm';
    }
    return '';
  }
}
